import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  message,
  Alert,
  Row,
  Col,
  Spin,
} from "antd";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BASE_URL, RoleEnum } from "../../Constants";
import AppContext from "../../AppContext";
import { toast } from "react-toastify";
import { Typography } from "antd";
import {
  GiftOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import CouponValidator from "../coupons/CouponValidator";
import { EditFormContext } from "./PublicEmployeeEditForm";

const { Title } = Typography;

const SubscriptionDetails = () => {
  const { formData: employeeData, setFormData: setEmployeeData } =
    useContext(EditFormContext);
  const { t } = useTranslation();
  const { profile } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [planOptions, setPlanOptions] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const loadPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/Lookup/plans`);
      if (res.status === 200) {
        setPlanOptions(res.data);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      message.error("Failed to load subscription plans. Please try again.");
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  // Handle URL parameters for payment success and reference number
  useEffect(() => {
    const success = searchParams.get("success");

    if (success === "false") {
      // Optionally show error message
      toast.error("Payment was not successful. Please try again.");
    }
  }, [searchParams]);

  const isAdmin = profile.role === RoleEnum.SUPER_ADMIN;

  // Function to clear payment state and URL parameters
  const clearPaymentState = () => {
    setSearchParams({});
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleCouponApplied = (coupon) => {
    setAppliedCoupon(coupon);

    if (coupon) {
      toast.success(
        `Discount coupon applied: ${
          coupon.discountType === "percentage"
            ? `${coupon.discountValue}% off`
            : `${coupon.discountValue} SAR off`
        }`
      );
    }
  };

  // Calculate final price with discounts
  const calculateFinalPrice = () => {
    if (!selectedPlan) return 0;

    const plan = planOptions.find((p) => p.id === selectedPlan);
    if (!plan) return 0;

    let finalPrice = plan.price;

    // Apply discount coupon if available
    if (appliedCoupon && appliedCoupon.type === "discount") {
      if (appliedCoupon.discountType === "percentage") {
        finalPrice = finalPrice * (1 - appliedCoupon.discountValue / 100);
      } else if (appliedCoupon.discountType === "fixedAmount") {
        finalPrice = Math.max(0, finalPrice - appliedCoupon.discountValue);
      }
    }

    return Math.round(finalPrice * 100) / 100; // Round to 2 decimal places
  };

  const calculateTotal = () => {
    if (!selectedPlan) return 0;

    const plan = planOptions.find((p) => p.id === selectedPlan);
    if (!plan) return 0;

    let total = plan.price;
    let discount = 0;

    // Use appliedCoupon for discount calculations
    if (appliedCoupon && appliedCoupon.type === "discount") {
      if (appliedCoupon.discountType === "percentage") {
        discount = (plan.price * appliedCoupon.discountValue) / 100;
      } else {
        discount = Math.min(appliedCoupon.discountValue, plan.price);
      }
    }

    return {
      originalPrice: plan.price,
      discount,
      total: total - discount,
      planName: plan.name,
    };
  };

  const handleSubscriptionSubmit = async (values, event) => {
    // Prevent form default submission behavior
    if (event) {
      event.preventDefault();
    }

    if (!selectedPlan) {
      message.error(t("hardcodedTexts.pleaseSelectPlan"));
      return;
    }

    setLoading(true);
    setPaymentLoading(true);

    try {
      // Calculate final price with discounts
      const finalPrice = calculateFinalPrice();
      const plan = planOptions.find((p) => p.id === selectedPlan);

      // Prepare payment data according to PaymentRequestDto structure.
      // productType "digital-card" routes this through the server-authoritative path: the backend
      // recomputes the plan price and any coupon discount itself (planAmount/finalAmount below are
      // no longer trusted for the charge) and, when a coupon covers the full price, completes the
      // renewal without ClickPay.
      const paymentData = {
        employeeId: employeeData.id,
        fullName: employeeData.fullName || employeeData.name || "",
        email: employeeData.email || "",
        phone: employeeData.phone || employeeData.phoneNumber || "",
        planAmount: plan.price,
        finalAmount: finalPrice,
        planId: selectedPlan,
        productPackageId: selectedPlan,
        productType: "digital-card",
        discountCode: appliedCoupon ? appliedCoupon.code : null,
      };

      // Call your existing click pay integration
      const paymentResponse = await axios.post(
        `${BASE_URL}/Payment/initiate`,
        paymentData
      );

      if (paymentResponse.data.success) {
        // requiresPayment === false => the coupon covered the full price; the renewal was
        // completed server-side and we go straight to the result page.
        if (paymentResponse.data.requiresPayment === false) {
          window.location.href = paymentResponse.data.redirectUrl;
        } else {
          window.location.href = paymentResponse.data.paymentUrl;
        }
      } else {
        toast.error(t("hardcodedTexts.failedInitiatePayment"));
      }
    } catch (error) {
      console.error("Payment/Subscription error:", error);

      // Navigate to subscription with failure parameters
      const params = new URLSearchParams({
        success: "false",
        error: error.response?.data || t("hardcodedTexts.paymentFailed"),
      });

      navigate(`/subscription?${params.toString()}`, { replace: true });

      if (error.response) {
        toast.error(`Error: ${error.response.data}`);
      } else {
        toast.error(t("hardcodedTexts.unexpectedError"));
      }
    } finally {
      setLoading(false);
      setPaymentLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      const response = await axios.put(
        `${BASE_URL}/PublicEmployee/update-subscription/${employeeData.id}`,
        null,
        {
          params: {
            newSubDate: new Date(employeeData.expirationDate).toISOString(), // or format like 'YYYY-MM-DD'
          },
        }
      );
      toast.success(t("hardcodedTexts.subscriptionUpdatedSuccessfully"));
      setEmployeeData((pre) => ({ ...pre, isTrial: false }));
      setIsEditing(false);
    } catch (error) {
      if (error.response) {
        toast.error(`Error: ${error.response.data}`); // shows bad request / not found message
      } else {
        toast.error(t("hardcodedTexts.unexpectedError"));
      }
    }
  };

  // Employee View - Includes editing capabilities
  const EmployeeView = () => {
    const subscriptionType = employeeData?.isTrial
      ? t("Trial")
      : employeeData.planId
      ? planOptions.find((plan) => plan.id === employeeData.planId)?.name
      : t("hardcodedTexts.nA");
    const subscriptionClass = employeeData?.isTrial
      ? "text-orange-500"
      : "text-green-600";

    // Check if subscription is expired
    const isSubscriptionExpired = () => {
      if (!employeeData?.expirationDate) return true;
      const expirationDate = new Date(employeeData.expirationDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      return expirationDate < today;
    };

    const subscriptionExpired = isSubscriptionExpired();

    return (
      <div className="bg-white rounded-lg shadow md:p-6 p-3">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          {t("Current Subscription")}
        </h2>
        <div className="mb-4 p-4 bg-gray-50 rounded-md">
          <div className="mb-2">
            <span className="font-medium text-gray-700">
              {t("Subscription Type")}:
            </span>{" "}
            <span className={`${subscriptionClass} font-medium`}>
              {subscriptionType}
            </span>
          </div>

          <div className="mb-2">
            <span className="font-medium text-gray-700">
              {t("Expiration Date")}:
            </span>{" "}
            <span
              className={`text-gray-900 ${
                subscriptionExpired ? "text-red-600 font-semibold" : ""
              }`}
            >
              {employeeData?.expirationDate
                ? formatDate(employeeData.expirationDate)
                : t("hardcodedTexts.nA")}
            </span>
          </div>

          {subscriptionExpired && !isAdmin && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <span className="text-red-600 font-medium">
                  ⚠️ {t("Subscription Expired")}
                </span>
              </div>
              <p className="text-red-600 text-sm mt-1">
                {t("hardcodedTexts.subscriptionExpiredWarning")}
              </p>
            </div>
          )}
        </div>
        {subscriptionExpired && !isAdmin && (
          <>
            <div className="mb-6">
              <Title level={4} className="mb-4">
                {t("hardcodedTexts.choosePlan")}
              </Title>
              {plansLoading ? (
                <div className="text-center py-8">
                  <Spin size="large" />
                  <p className="mt-4 text-gray-500">
                    {t("hardcodedTexts.loadingPlans")}
                  </p>
                </div>
              ) : planOptions.length === 0 ? (
                <div className="text-center py-8">
                  <Alert
                    message={t("hardcodedTexts.noPlansAvailable")}
                    description={t("hardcodedTexts.noPlansDescription")}
                    type="warning"
                    showIcon
                  />
                </div>
              ) : (
                <Row gutter={16}>
                  {planOptions.map((plan) => (
                    <Col xs={24} md={12} key={plan.id}>
                      <Card
                        className={`border-2 h-full cursor-pointer transition-all duration-200 ${
                          selectedPlan === plan.id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-blue-300"
                        }`}
                        onClick={() => {
                          setSelectedPlan(plan.id);
                          form.setFieldValue("plan", plan.id);
                        }}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <Title level={5} className="mb-0">
                            {plan.name}
                          </Title>
                          {selectedPlan === plan.id && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                        </div>
                        <div className="text-2xl font-bold mb-3">
                          {plan.price} {plan.currency}/{plan.period}
                        </div>
                        <ul className="space-y-1">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="text-sm text-gray-600">
                              • {feature}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </div>

            {!plansLoading && planOptions.length > 0 && (
              <div className="mb-6">
                <Divider orientation="left">
                  {t("hardcodedTexts.applyDiscountCoupon")}
                </Divider>

                <div className="mb-4">
                  <Title level={5} className="text-green-600 mb-2">
                    <CheckCircleOutlined className="mr-2" />
                    {t("hardcodedTexts.discountCode")}
                  </Title>
                  <CouponValidator
                    onCouponApplied={handleCouponApplied}
                    type="discount"
                    placeholder={t("hardcodedTexts.enterDiscountCode")}
                  />
                  {appliedCoupon && (
                    <Alert
                      message={`${t("hardcodedTexts.discount")} ${t(
                        "coupons.couponApplied"
                      )}!`}
                      description={`${
                        appliedCoupon.discountType === "percentage"
                          ? `${appliedCoupon.discountValue}% discount`
                          : `${appliedCoupon.discountValue} SAR discount`
                      } will be applied to your payment.`}
                      type={"info"}
                      showIcon
                      closable
                      onClose={() => handleCouponApplied(null)}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Pricing Summary */}
            {!plansLoading && selectedPlan && (
              <div className="mb-6">
                <Card className="bg-gray-50">
                  <Title level={5}>{t("hardcodedTexts.orderSummary")}</Title>
                  {(() => {
                    const pricing = calculateTotal();
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>{pricing.planName}</span>
                          <span>{pricing.originalPrice} SAR</span>
                        </div>
                        {appliedCoupon &&
                          appliedCoupon.type === "discount" &&
                          pricing.discount > 0 && (
                            <div className="flex justify-between text-green-600">
                              <span>
                                {t("hardcodedTexts.discount")} (
                                {appliedCoupon.code})
                              </span>
                              <span>-{pricing.discount.toFixed(2)} SAR</span>
                            </div>
                          )}
                        <Divider className="my-2" />
                        {appliedCoupon && (
                          <div className="flex justify-between font-bold text-lg">
                            <span>{t("hardcodedTexts.total")}</span>
                            <span>{pricing.total.toFixed(2)} SAR</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </Card>
              </div>
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubscriptionSubmit}
            >
              <Form.Item
                name="plan"
                rules={[
                  {
                    required: true,
                    message: t("hardcodedTexts.pleaseSelectPlan"),
                  },
                ]}
                hidden
              >
                <Input />
              </Form.Item>

              <div className="flex justify-between mt-6">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="text-white disabled:text-white"
                  loading={loading || paymentLoading}
                  disabled={!selectedPlan || plansLoading}
                  size="large"
                  onClick={(e) => {
                    e.preventDefault();
                    form
                      .validateFields()
                      .then((values) => {
                        handleSubscriptionSubmit(values, e);
                      })
                      .catch((errorInfo) => {
                        console.log("Validation Failed:", errorInfo);
                      });
                  }}
                >
                  {appliedCoupon && appliedCoupon.type === "discount"
                    ? `Proceed to Payment (${calculateFinalPrice().toFixed(
                        2
                      )} SAR)`
                    : t("hardcodedTexts.proceedToPayment")}
                </Button>
              </div>
            </Form>

            <div className="mt-8 text-center text-gray-500 text-sm">
              <p>
                {t("hardcodedTexts.needHelpSubscription")}{" "}
                <a
                  href={"https://future-horses.com/contact/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                  aria-label={t("hardcodedTexts.contactSupport")}
                >
                  {t("hardcodedTexts.contactSupport")}
                </a>
              </p>
              <p className="mt-2 text-xs">
                {t("hardcodedTexts.securePaymentProcessing")}
              </p>
            </div>
          </>
        )}{" "}
        {!subscriptionExpired && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <CheckCircleOutlined className="text-green-600 mr-2" />
              <span className="text-green-700 font-medium">
                {t("hardcodedTexts.subscriptionActive")}
              </span>
            </div>
            <p className="text-green-700 text-sm mt-1">
              {t("hardcodedTexts.subscriptionActiveDescription")}{" "}
              <span className="font-semibold">
                {employeeData?.expirationDate
                  ? formatDate(employeeData.expirationDate)
                  : t("hardcodedTexts.nA")}
              </span>
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto md:px-4 px-2 md:py-8 py-4 pt-5">
      {/* Show error alert if payment failed */}
      {searchParams.get("success") === "false" && (
        <Alert
          message={t("hardcodedTexts.paymentFailed")}
          description={
            searchParams.get("error") ||
            t("hardcodedTexts.paymentFailedDescription")
          }
          type="error"
          showIcon
          closable
          onClose={clearPaymentState}
          className="mb-4"
        />
      )}

      <EmployeeView />
    </div>
  );
};

export default SubscriptionDetails;
